import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosConfig';
import { canSalesTarget } from './salesTargetAccess';
import UploadReportModal from './modals/UploadReportModal';
import BulkUploadSalesReportModal from './modals/BulkUploadSalesReportModal';
import EditReportModal from './modals/EditReportModal';
import './SalesReportsTab.css';

const MONTHS = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

const formatNum = (n) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n ?? 0);

// ── Icon SVGs (no emojis) ────────────────────────────────────
const IconUpload = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="17 8 12 3 7 8"/>
        <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
);
const IconEdit = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);
const IconDelete = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
);
const IconRefresh = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
);

// ── Main Component ───────────────────────────────────────────
const SalesReportsTab = ({ onDataChange }) => {
    const { user } = useAuth();
    // The tab opens for 'create' OR 'edit'; each button needs its own action,
    // matching the method the server maps it to.
    const canCreate = canSalesTarget(user, 'create');
    const canEdit = canSalesTarget(user, 'edit');
    const canDelete = canSalesTarget(user, 'delete');
    const [reports,   setReports]   = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [error,     setError]     = useState(null);
    const [companies, setCompanies] = useState([]);
    const [branches,  setBranches]  = useState([]);
    const [salesmen,  setSalesmen]  = useState([]);
    const [uploadStatus, setUploadStatus] = useState({ 
        completeDays: [], 
        partialDays: [], 
        missingDays: [], 
        dayDetails: {} 
    });
    
    // Pagination state
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        total: 0,
        grandTotalSales: 0,
        limit: 50
    });

    const [filters, setFilters] = useState({
        companyId:  '',
        branchId:   '',
        salesmanId: '',
        month:  new Date().getMonth() + 1,
        year:   CURRENT_YEAR,
        days:   [], // Changed from 'day' to 'days' array
    });

    const [showUpload,     setShowUpload]     = useState(false);
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [showEdit,       setShowEdit]       = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);

    useEffect(() => { fetchCompanies(); fetchBranches(); fetchSalesmen(); }, []);
    useEffect(() => { fetchReports(); fetchUploadStatus(); }, [filters, pagination.currentPage]);
    useEffect(() => { fetchBranches(); }, [filters.companyId]);
    useEffect(() => { fetchSalesmen(); }, [filters.branchId]);

    const fetchCompanies = async () => {
        try {
            const res = await axiosInstance.get('/api/sales-target/organizations/tree');
            if (res.data.success) {
                const extract = (nodes) => {
                    let list = [];
                    nodes.forEach(n => {
                        if (n.type === 'company') list.push(n);
                        if (n.children) list = [...list, ...extract(n.children)];
                    });
                    return list;
                };
                setCompanies(extract(res.data.data));
            }
        } catch { /* silently ignore */ }
    };

    const fetchBranches = async () => {
        try {
            const res = await axiosInstance.get('/api/sales-target/organizations/tree');
            if (res.data.success) {
                const extract = (nodes, parentCompanyId = null) => {
                    let list = [];
                    nodes.forEach(n => {
                        if (n.type === 'branch') {
                            // If filtering by company, only include branches under that company
                            if (!filters.companyId || parentCompanyId === filters.companyId) {
                                list.push(n);
                            }
                        }
                        if (n.children) {
                            list = [...list, ...extract(n.children, n.type === 'company' ? n._id : parentCompanyId)];
                        }
                    });
                    return list;
                };
                setBranches(extract(res.data.data));
            }
        } catch { /* silently ignore */ }
    };

    const fetchSalesmen = async () => {
        try {
            const params = { active: true };
            if (filters.branchId) params.branchId = filters.branchId;
            
            const res = await axiosInstance.get('/api/sales-target/salesmen', { params });
            if (res.data.success) setSalesmen(res.data.data || []);
        } catch { /* silently ignore */ }
    };

    const fetchReports = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = { 
                month: filters.month, 
                year: filters.year,
                page: pagination.currentPage,
                limit: pagination.limit
            };
            if (filters.companyId)  params.companyId  = filters.companyId;
            if (filters.branchId)   params.branchId   = filters.branchId;
            if (filters.salesmanId) params.salesmanId = filters.salesmanId;
            if (filters.days && filters.days.length > 0) {
                params.days = filters.days.join(','); // Convert array to comma-separated string
            }

            const res = await axiosInstance.get('/api/sales-target/reports', { params });
            if (res.data.success) {
                setReports(res.data.data || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: res.data.currentPage || 1,
                    totalPages: res.data.totalPages || 1,
                    total: res.data.total || 0,
                    grandTotalSales: res.data.grandTotalSales || 0
                }));
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const fetchUploadStatus = async () => {
        try {
            // Only fetch if company or branch is selected
            if (!filters.companyId && !filters.branchId) {
                setUploadStatus({ 
                    completeDays: [], 
                    partialDays: [], 
                    missingDays: [], 
                    dayDetails: {},
                    requiresFilter: true
                });
                return;
            }

            const params = { month: filters.month, year: filters.year };
            if (filters.companyId) params.companyId = filters.companyId;
            if (filters.branchId)  params.branchId  = filters.branchId;

            const res = await axiosInstance.get('/api/sales-target/reports/upload-status', { params });
            if (res.data.success) {
                setUploadStatus({ ...res.data.data, requiresFilter: false });
            }
        } catch (err) {
            console.error('Failed to fetch upload status:', err);
            // Don't show error to user, just use empty status
            setUploadStatus({ 
                completeDays: [], 
                partialDays: [], 
                missingDays: [], 
                dayDetails: {},
                requiresFilter: false
            });
        }
    };

    // Wrapper function for successful operations that should refresh tree
    const handleSuccessWithTreeRefresh = () => {
        fetchReports();
        fetchUploadStatus();
        if (onDataChange) {
            onDataChange();
        }
    };

    const handleEdit = (report) => { setSelectedReport(report); setShowEdit(true); };

    const handleDelete = async (reportId) => {
        if (!window.confirm('Delete this report? This action cannot be undone.')) return;
        try {
            await axiosInstance.delete(`/api/sales-target/reports/${reportId}`);
            fetchReports();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to delete report');
        }
    };

    const setFilter = (key, val) => {
        setFilters(prev => {
            const newFilters = { ...prev, [key]: val };
            // Reset dependent filters when parent changes
            if (key === 'companyId') {
                newFilters.branchId = '';
                newFilters.salesmanId = '';
            } else if (key === 'branchId') {
                newFilters.salesmanId = '';
            }
            return newFilters;
        });
        // Reset to page 1 when filters change
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Handle day selection toggle
    const toggleDay = (day) => {
        setFilters(prev => {
            const currentDays = prev.days || [];
            const dayNum = parseInt(day);
            
            if (currentDays.includes(dayNum)) {
                // Remove day if already selected
                return { ...prev, days: currentDays.filter(d => d !== dayNum) };
            } else {
                // Add day if not selected
                return { ...prev, days: [...currentDays, dayNum].sort((a, b) => a - b) };
            }
        });
        // Reset to page 1 when days change
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Clear all selected days
    const clearDays = () => {
        setFilters(prev => ({ ...prev, days: [] }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Select all days
    const selectAllDays = () => {
        setFilters(prev => ({ ...prev, days: Array.from({ length: 31 }, (_, i) => i + 1) }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Pagination handlers
    const goToPage = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    const nextPage = () => {
        if (pagination.currentPage < pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
        }
    };

    const prevPage = () => {
        if (pagination.currentPage > 1) {
            setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
        }
    };

    // Use server-side totals so the cards reflect the ENTIRE filtered set, not just the
    // current page of 50. (reports.reduce only saw the loaded page — that was the bug.)
    const totalSales   = pagination.grandTotalSales;
    const totalReports = pagination.total;

    // Loading state
    if (loading && reports.length === 0) {
        return (
            <div className="sales-reports-tab">
                <div className="reports-loading">
                    <div className="spinner" />
                    <p>Loading reports…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="sales-reports-tab">
            {/* Header */}
            <div className="reports-header">
                <h2>Sales Reports</h2>
                {canCreate && (
                    <div className="upload-buttons">
                        <button className="btn-secondary" onClick={() => setShowBulkUpload(true)}>
                            <IconUpload /> Bulk Upload
                        </button>
                        <button className="btn-primary" onClick={() => setShowUpload(true)}>
                            <IconUpload /> Upload Report
                        </button>
                    </div>
                )}
            </div>

            {/* Summary cards (shown when there is data) */}
            {reports.length > 0 && (
                <div className="reports-summary-cards">
                    <div className="reports-summary-card">
                        <span className="sc-label">Total Reports</span>
                        <span className="sc-value">{totalReports}</span>
                    </div>
                    <div className="reports-summary-card">
                        <span className="sc-label">Total Sales</span>
                        <span className="sc-value">{formatNum(totalSales)}</span>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="reports-filters">
                <div className="filter-group">
                    <label>Company</label>
                    <select value={filters.companyId} onChange={e => setFilter('companyId', e.target.value)}>
                        <option value="">All Companies</option>
                        {companies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Branch</label>
                    <select value={filters.branchId} onChange={e => setFilter('branchId', e.target.value)}>
                        <option value="">All Branches</option>
                        {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Salesman</label>
                    <select value={filters.salesmanId} onChange={e => setFilter('salesmanId', e.target.value)}>
                        <option value="">All Salesmen</option>
                        {salesmen.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Year</label>
                    <select value={filters.year} onChange={e => setFilter('year', parseInt(e.target.value))}>
                        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Month</label>
                    <select value={filters.month} onChange={e => setFilter('month', parseInt(e.target.value))}>
                        {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                    </select>
                </div>
                <button className="refresh-button" onClick={fetchReports}>
                    <IconRefresh /> Refresh
                </button>
            </div>

            {/* Day Selection */}
            <div className="day-filter-section">
                <div className="day-filter-header">
                    <label>Filter by Days:</label>
                    <div className="day-filter-actions">
                        <button className="btn-link" onClick={selectAllDays}>Select All</button>
                        <button className="btn-link" onClick={clearDays}>Clear All</button>
                        <span className="selected-count">
                            {filters.days.length > 0 ? `${filters.days.length} day(s) selected` : 'All days'}
                        </span>
                    </div>
                </div>
                
                {/* Show message when no company/branch selected */}
                {uploadStatus.requiresFilter ? (
                    <div className="filter-required-message">
                        <span className="info-icon">ℹ️</span>
                        <span>Please select a Company or Branch to see upload status for each day</span>
                    </div>
                ) : (
                    <>
                        {/* Legend */}
                        <div className="day-status-legend">
                            <span className="legend-item">
                                <span className="legend-box complete"></span>
                                <span className="legend-text">Complete (All salesmen)</span>
                            </span>
                            <span className="legend-item">
                                <span className="legend-box partial"></span>
                                <span className="legend-text">Partial (Some salesmen)</span>
                            </span>
                            <span className="legend-item">
                                <span className="legend-box missing"></span>
                                <span className="legend-text">Missing (No reports)</span>
                            </span>
                            <span className="legend-item">
                                <span className="legend-box selected"></span>
                                <span className="legend-text">Selected</span>
                            </span>
                        </div>
                    </>
                )}

                <div className="day-checkboxes">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => {
                        const isComplete = uploadStatus.completeDays.includes(day);
                        const isPartial = uploadStatus.partialDays.includes(day);
                        const isMissing = uploadStatus.missingDays.includes(day);
                        const details = uploadStatus.dayDetails?.[day];
                        
                        let dayClass = 'day-checkbox';
                        let statusIcon = '';
                        let tooltipText = 'No data';
                        
                        if (isComplete) {
                            dayClass += ' complete';
                            statusIcon = '✓';
                            tooltipText = details 
                                ? `Complete: ${details.uploaded}/${details.expected} salesmen (${details.percentage}%)` 
                                : 'Complete';
                        } else if (isPartial) {
                            dayClass += ' partial';
                            statusIcon = '⚠';
                            tooltipText = details 
                                ? `Partial: ${details.uploaded}/${details.expected} salesmen (${details.percentage}%)` 
                                : 'Partial';
                        } else if (isMissing) {
                            dayClass += ' missing';
                            statusIcon = '✗';
                            tooltipText = details 
                                ? `Missing: 0/${details.expected} salesmen` 
                                : 'No reports';
                        }
                        
                        return (
                            <label 
                                key={day} 
                                className={dayClass}
                                title={tooltipText}
                            >
                                <input
                                    type="checkbox"
                                    checked={filters.days.includes(day)}
                                    onChange={() => toggleDay(day)}
                                />
                                <span>{day}</span>
                                {statusIcon && <span className="status-indicator">{statusIcon}</span>}
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Error */}
            {error && <div className="error-message">⚠ {error}</div>}

            {/* Table */}
            <div className="reports-table-container">
                {reports.length === 0 ? (
                    <div className="empty-state">
                        <p>No reports found for the selected filters.</p>
                        {canCreate && (
                            <div className="empty-state-buttons">
                                <button className="btn-secondary" onClick={() => setShowBulkUpload(true)}>
                                    <IconUpload /> Bulk Upload
                                </button>
                                <button className="btn-primary" onClick={() => setShowUpload(true)}>
                                    <IconUpload /> Upload First Report
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <table className="reports-table">
                            <thead>
                                <tr>
                                    <th>Salesman</th>
                                    <th>Company</th>
                                    <th>Branch</th>
                                    <th>Period</th>
                                    <th className="align-right">Total Sales</th>
                                    <th>Uploaded By</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.map(r => (
                                    <tr key={r._id}>
                                        <td>
                                            <div className="salesman-cell">
                                                <span className="salesman-name">{r.salesmanName}</span>
                                            </div>
                                        </td>
                                        <td>{r.companyName || 'N/A'}</td>
                                        <td>{r.branchName}</td>
                                        <td>
                                            {r.day ? `${r.day} ` : ''}{MONTHS[(r.month || 1) - 1]} {r.year}
                                        </td>
                                        <td className="align-right">
                                            <span className="sales-amount">{formatNum(r.totalSales)}</span>
                                        </td>
                                        <td>
                                            <div className="uploader-cell">
                                                <span>{r.uploadedByName}</span>
                                                <small>{new Date(r.createdAt).toLocaleDateString()}</small>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                {canEdit && <button className="btn-icon" onClick={() => handleEdit(r)} title="Edit"><IconEdit /></button>}
                                                {canDelete && <button className="btn-icon delete" onClick={() => handleDelete(r._id)} title="Delete"><IconDelete /></button>}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="pagination-container">
                                <div className="pagination-info">
                                    Showing {reports.length} of {pagination.total} reports
                                </div>
                                <div className="pagination-controls">
                                    <button 
                                        className="pagination-btn" 
                                        onClick={prevPage}
                                        disabled={pagination.currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                    
                                    <div className="pagination-pages">
                                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => {
                                            // Show first page, last page, current page, and pages around current
                                            const showPage = page === 1 || 
                                                           page === pagination.totalPages || 
                                                           Math.abs(page - pagination.currentPage) <= 1;
                                            
                                            const showEllipsis = (page === 2 && pagination.currentPage > 3) ||
                                                               (page === pagination.totalPages - 1 && pagination.currentPage < pagination.totalPages - 2);
                                            
                                            if (showEllipsis) {
                                                return <span key={page} className="pagination-ellipsis">...</span>;
                                            }
                                            
                                            if (!showPage) return null;
                                            
                                            return (
                                                <button
                                                    key={page}
                                                    className={`pagination-page ${page === pagination.currentPage ? 'active' : ''}`}
                                                    onClick={() => goToPage(page)}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    
                                    <button 
                                        className="pagination-btn" 
                                        onClick={nextPage}
                                        disabled={pagination.currentPage === pagination.totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modals */}
            <UploadReportModal
                show={showUpload}
                onClose={() => setShowUpload(false)}
                onSuccess={handleSuccessWithTreeRefresh}
                companies={companies}
            />
            <BulkUploadSalesReportModal
                show={showBulkUpload}
                onClose={() => setShowBulkUpload(false)}
                onSuccess={handleSuccessWithTreeRefresh}
                companies={companies}
            />
            <EditReportModal
                show={showEdit}
                report={selectedReport}
                onClose={() => setShowEdit(false)}
                onSuccess={handleSuccessWithTreeRefresh}
            />
        </div>
    );
};

export default SalesReportsTab;
